import React, { useEffect, useState } from "react";
const API = process.env.REACT_APP_API_BASE_URL ?? "https://zoompay.co.ke";
const getToken = () => localStorage.getItem("adminToken");

// PRICING_V2. One model: live rate x (1 - FX markup) for SNPN and SNPL; SNPL
// fee = % + fixed; SNPN small-transfer fee; airtime/bill own discounts;
// extension fee; wallet and 24h caps. Every row saves with a reason and is
// audited; large percentage moves ask for confirmation.
const GROUPS = [
  { title: "Exchange rate", keys: ["fx_markup_remittance", "fx_discount_airtime", "fx_discount_bill_pay", "fx_rate_max_age_hours"] },
  { title: "Send Now Pay Now (SNPN)", keys: ["snpn_small_transfer_fee", "snpn_small_transfer_threshold"], legacy: ["snpn_multi_recipient_fee"] },
  { title: "Send Now Pay Later (SNPL)", keys: ["snpl_deferred_settlement_fee", "snpl_fixed_fee", "snpl_fixed_fee_large", "snpl_fixed_fee_large_from", "snpl_extension_fee"], legacy: ["snpl_multi_recipient_fee"] },
  { title: "Limits", keys: ["wallet_max_balance_cad", "send_24h_max_cad"] },
];
const LEGACY_META = {
  snpn_multi_recipient_fee: { unit: "cad", min: 0, max: 50, label: "Fee per additional recipient (SNPN)" },
  snpl_multi_recipient_fee: { unit: "cad", min: 0, max: 50, label: "Fee per additional recipient (SNPL)" },
};

const fmt = (unit, v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (unit === "pct") return `${(n * 100).toFixed(2)}%`;
  if (unit === "cad") return `CAD ${n.toFixed(2)}`;
  return `${n} ${unit}`;
};
const toInput = (unit, v) => (unit === "pct" ? (Number(v || 0) * 100).toFixed(2) : String(v ?? ""));
const fromInput = (unit, s) => (unit === "pct" ? Number(s) / 100 : Number(s));
// Plain, unsigned numbers only: no blanks (which would become 0), no minus sign,
// no exponent; percent and CAD to 2 decimals, hours whole, rates to 4 decimals.
const PLAIN = { pct: /^\d+(\.\d{1,2})?$/, cad: /^\d+(\.\d{1,2})?$/, hours: /^\d+$/, rate: /^\d+(\.\d{1,4})?$/ };
const plainError = (unit, s) => (PLAIN[unit] || PLAIN.cad).test(String(s).trim()) ? null
  : unit === "hours" ? "Enter a whole number" : unit === "rate" ? "Enter a positive number, up to 4 decimals"
  : "Enter a positive number, up to 2 decimals (no minus sign)";
const kes = (n) => Number(n).toLocaleString("en-CA", { maximumFractionDigits: 0 });

async function api(path, method, body) {
  const res = await fetch(`${API}/api/v2/cockpit${path}`, {
    method,
    headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  return { status: res.status, data: await res.json().catch(() => ({})) };
}

function SettingRow({ k, meta, value, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async (confirm = false) => {
    const bad = plainError(meta.unit, val);
    if (bad) { setMsg({ ok: false, text: bad }); return; }
    const num = fromInput(meta.unit, String(val).trim());
    if (!Number.isFinite(num) || num < meta.min || num > meta.max) {
      setMsg({ ok: false, text: `Must be between ${fmt(meta.unit, meta.min)} and ${fmt(meta.unit, meta.max)}` });
      return;
    }
    if (reason.trim().length < 10) { setMsg({ ok: false, text: "Reason: at least 10 characters" }); return; }
    setBusy(true);
    try {
      const { status, data } = await api("/fx-config/discount", "PUT", { key: k, value: num, reason, confirm });
      if (status === 409 && data.code === "CONFIRM_REQUIRED") {
        if (window.confirm(data.error)) return save(true);
        setMsg({ ok: false, text: "Not changed" });
      } else if (data.ok) {
        setMsg({ ok: true, text: `Saved: ${fmt(meta.unit, data.value)}` });
        setEditing(false); setReason(""); onSaved();
      } else setMsg({ ok: false, text: data.error || "Failed" });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="font-medium text-gray-800">{meta.label}</div>
        <div className="text-xs text-gray-400">{k}</div>
      </td>
      <td className="px-4 py-3 font-bold text-blue-700 whitespace-nowrap">{fmt(meta.unit, value)}</td>
      <td className="px-4 py-3">
        {editing ? (
          <div className="flex items-center gap-2 flex-wrap">
            <input type="text" inputMode="decimal" value={val} onChange={(e) => setVal(e.target.value)}
              className="w-28 border rounded-lg px-3 py-1.5 text-sm" aria-label={`${meta.label} new value`} />
            <span className="text-gray-400 text-sm">{meta.unit === "pct" ? "%" : meta.unit === "cad" ? "CAD" : meta.unit}</span>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (required)" className="border rounded-lg px-2 py-1.5 text-sm w-48" />
            <button onClick={() => save(false)} disabled={busy}
              className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
              {busy ? "..." : "Save"}
            </button>
            <button onClick={() => { setEditing(false); setMsg(null); }}
              className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-300">Cancel</button>
          </div>
        ) : (
          <button onClick={() => { setEditing(true); setVal(toInput(meta.unit, value)); setMsg(null); }}
            className="text-sm bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100 font-medium">
            Edit
          </button>
        )}
        {msg && <span className={"ml-2 text-xs font-medium " + (msg.ok ? "text-green-600" : "text-red-600")}>{msg.text}</span>}
      </td>
    </tr>
  );
}

function Preview({ p }) {
  if (!p) return null;
  if (!p.ok) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
        <b>Pricing is currently refused:</b> {p.error} Refresh rates or set a manual rate below.
      </div>
    );
  }
  return (
    <div className="bg-white border rounded-lg shadow-sm">
      <div className="px-6 py-4 border-b">
        <h2 className="font-semibold text-gray-800">What customers get right now (CAD → KES)</h2>
        <p className="text-xs text-gray-400 mt-0.5">Live rate as of {new Date(p.updatedAt).toLocaleString()}.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 text-sm">
        <div><div className="text-gray-500">Market rate</div><div className="text-xl font-bold">{Number(p.marketRate).toFixed(4)}</div></div>
        <div><div className="text-gray-500">SNPN / SNPL rate</div><div className="text-xl font-bold text-green-700">{Number(p.remittanceRate).toFixed(4)}</div><div className="text-xs text-gray-400">markup {(p.markup * 100).toFixed(2)}%</div></div>
        <div><div className="text-gray-500">Airtime rate</div><div className="text-xl font-bold">{Number(p.airtimeRate).toFixed(4)}</div></div>
        <div><div className="text-gray-500">Bill pay rate</div><div className="text-xl font-bold">{Number(p.billPayRate).toFixed(4)}</div></div>
      </div>
      <table className="w-full text-sm border-t">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-gray-600 font-medium">Customer sends</th>
            <th className="px-4 py-2 text-left text-gray-600 font-medium">Recipient gets</th>
            <th className="px-4 py-2 text-left text-gray-600 font-medium">SNPN pays now</th>
            <th className="px-4 py-2 text-left text-gray-600 font-medium">SNPL fee</th>
            <th className="px-4 py-2 text-left text-gray-600 font-medium">SNPL due</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {(p.examples || []).map((e) => (
            <tr key={e.cad}>
              <td className="px-4 py-2">CAD {e.cad.toFixed(2)}</td>
              <td className="px-4 py-2 font-medium">KES {kes(e.recipientGetsKes)}</td>
              <td className="px-4 py-2">CAD {(e.cad + e.snpnFee).toFixed(2)}{e.snpnFee > 0 ? ` (incl. ${e.snpnFee.toFixed(2)} fee)` : ""}</td>
              <td className="px-4 py-2">CAD {e.snplFee.toFixed(2)}</td>
              <td className="px-4 py-2">CAD {e.snplTotalDue.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CockpitFXConfig() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [editRate, setEditRate] = useState(null);
  const [newRate, setNewRate] = useState("");
  const [rateReason, setRateReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api("/fx-config", "GET").then(({ data: d }) => { if (d.ok) setData(d); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const refreshRates = async () => {
    setRefreshing(true); setRefreshResult(null);
    try { const { data: d } = await api("/fx-rates/refresh", "POST"); setRefreshResult(d); if (d.ok) load(); }
    catch (e) { setRefreshResult({ ok: false, error: e.message }); }
    finally { setRefreshing(false); }
  };

  const saveRate = async (rate, confirm = false) => {
    const bad = plainError("rate", newRate);
    if (bad || Number(newRate) <= 0) { alert(bad || "Rate must be above 0"); return; }
    if (rateReason.trim().length < 10) { alert("Reason: at least 10 characters"); return; }
    setSaving(true);
    try {
      const { status, data: d } = await api("/fx-config/rate", "PUT",
        { fromCurrency: rate.fromCurrency, toCurrency: rate.toCurrency, rate: String(newRate).trim(), reason: rateReason, confirm });
      if (status === 409 && d.code === "CONFIRM_REQUIRED") { if (window.confirm(d.error)) return saveRate(rate, true); }
      else if (d.ok) { setEditRate(null); setNewRate(""); setRateReason(""); load(); }
      else alert("Error: " + d.error);
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const release = async (rate) => {
    if (!window.confirm(`Release the manual ${rate.fromCurrency}→${rate.toCurrency} rate and go back to the live feed?`)) return;
    const { data: d } = await api("/fx-config/rate/release", "POST", { fromCurrency: rate.fromCurrency, toCurrency: rate.toCurrency });
    if (!d.ok) alert("Error: " + d.error);
    load();
  };

  if (loading && !data) return <div className="p-8 text-gray-400">Loading pricing...</div>;
  if (!data) return <div className="p-8 text-red-600">Could not load pricing.</div>;

  const byKey = Object.fromEntries((data.settings || []).map((s) => [s.key, s]));
  const locks = data.locks || {};

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold">💱 Pricing</h1>
        <p className="text-gray-500 text-sm">
          Customer rate = live market rate × (1 − FX markup), the same for SNPN and SNPL. Changes apply to new quotes
          immediately; every change needs a reason and is recorded in the audit log.
        </p>
      </div>

      <Preview p={data.preview} />

      {GROUPS.map((g) => (
        <div key={g.title} className="bg-white border rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b"><h2 className="font-semibold text-gray-800">{g.title}</h2></div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-gray-600 font-medium">Setting</th>
                <th className="px-4 py-3 text-left text-gray-600 font-medium">Current</th>
                <th className="px-4 py-3 text-left text-gray-600 font-medium">Change</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {g.keys.filter((k) => byKey[k]).map((k) => (
                <SettingRow key={k} k={k} meta={byKey[k]} value={byKey[k].value} onSaved={load} />
              ))}
              {(g.legacy || []).map((k) => (
                <SettingRow key={k} k={k} meta={LEGACY_META[k]} value={(data.legacy || {})[k]} onSaved={load} />
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <div className="bg-white border rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b flex justify-between items-center gap-4 flex-wrap">
          <div>
            <h2 className="font-semibold text-gray-800">🌍 Live exchange rates</h2>
            <p className="text-xs text-gray-400 mt-0.5">Refreshed daily at 06:00 UTC. A manual rate stays until you release it.</p>
          </div>
          <button onClick={refreshRates} disabled={refreshing}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {refreshing ? "Refreshing..." : "Refresh now"}
          </button>
        </div>
        {refreshResult && (
          <div className={"px-6 py-2 text-xs " + (refreshResult.ok ? "text-green-700" : "text-red-700")}>
            {refreshResult.ok ? `Updated ${refreshResult.updated} rates` : `Refresh failed: ${refreshResult.error}`}
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Pair</th>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Market rate</th>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Updated</th>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Manual rate</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {(data.rates || []).filter((r) => r.toCurrency === "KES").map((r) => {
              const id = `${r.fromCurrency}_${r.toCurrency}`;
              return (
                <tr key={id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">
                    {r.fromCurrency} → {r.toCurrency}
                    {locks[id] && <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">manual</span>}
                  </td>
                  <td className="px-4 py-3 font-bold">{Number(r.rate).toFixed(4)}</td>
                  <td className="px-4 py-3 text-gray-500">{new Date(r.updatedAt || r.updated_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    {editRate === id ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <input type="text" inputMode="decimal" value={newRate} onChange={(e) => setNewRate(e.target.value)}
                          className="w-28 border rounded-lg px-3 py-1.5 text-sm" />
                        <input type="text" value={rateReason} onChange={(e) => setRateReason(e.target.value)}
                          placeholder="Reason (required)" className="border rounded-lg px-2 py-1.5 text-sm w-48" />
                        <button onClick={() => saveRate(r)} disabled={saving}
                          className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm disabled:opacity-50">{saving ? "..." : "Set"}</button>
                        <button onClick={() => setEditRate(null)} className="bg-gray-200 px-3 py-1.5 rounded-lg text-sm">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => { setEditRate(id); setNewRate(Number(r.rate).toFixed(4)); }}
                          className="text-sm bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100">Set manually</button>
                        {locks[id] && (
                          <button onClick={() => release(r)}
                            className="text-sm bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-lg hover:bg-amber-100">Release</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
